import { useState } from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import { XCard, AILoading } from "../../../../components";
import { Check } from "react-feather";
import { textXS } from "../../../../shared/constants";
import colors from "../../../../shared/constants/color";
import { ZapIcon } from "../../../../shared/images/icons";
import type { QuestionItem } from "../../../interviewAssistant/InterviewAssistant";
import { useWiserTheme } from "../../../../hooks/useWiserTheme";
import {
  getQuestionListItemSx,
  getQuestionListTextColor,
} from "../../../../shared/theme/wiserThemeHelpers";
import {
  centeredContentSx,
  centeredContentFillSx,
  columnGap8Sx,
  columnGap12Sx,
  iconCheckWrapSx,
  getAiQuestionTabsSx,
  getAiQuestionTabSx,
} from "../../style";

interface IProps {
  hrQuestions: QuestionItem[] | null;
  technicalQuestions: QuestionItem[] | null;
  onHrQuestionClick: (id: number) => void;
  onTechnicalQuestionClick: (id: number) => void;
  loading?: boolean;
  sx?: SxProps<Theme>;
}

const tabs = [
  { key: "hr", label: "İK Soruları" },
  { key: "technical", label: "Teknik Sorular" },
] as const;

type TabKey = (typeof tabs)[number]["key"];

const QuestionList = ({
  questions,
  onQuestionClick,
}: {
  questions: QuestionItem[] | null;
  onQuestionClick: (id: number) => void;
}) => {
  const { darkModeActive, tokens } = useWiserTheme();

  if (!questions || questions.length === 0) {
    return (
      <Box sx={centeredContentSx}>
        <Typography
          sx={textXS(
            400,
            darkModeActive ? tokens.textMuted : colors.neutral.N500,
          )}
        >
          Yeterli veri olmadığı için AI soruları oluşturulamadı.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={columnGap8Sx}>
      {questions.map((question, index) => (
        <Box
          key={question.id}
          sx={getQuestionListItemSx(darkModeActive, question.checked)}
          onClick={() => onQuestionClick(question.id)}
        >
          {question.checked && (
            <Box sx={iconCheckWrapSx}>
              <Check size={14} color={colors.green.G400} strokeWidth={3} />
            </Box>
          )}
          <Typography
            sx={textXS(
              400,
              getQuestionListTextColor(darkModeActive, question.checked),
            )}
          >
            {index + 1}. {question.question}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const AIQuestionListCard = ({
  hrQuestions,
  technicalQuestions,
  onHrQuestionClick,
  onTechnicalQuestionClick,
  loading = false,
  sx,
}: IProps) => {
  const [activeTab, setActiveTab] = useState<TabKey>("hr");
  const { darkModeActive, tokens } = useWiserTheme();

  return (
    <XCard
      title="AI Sorular"
      titleStyle="aiGradient"
      icon={<img src={ZapIcon} />}
      style="aiNeutral"
      sx={sx}
    >
      {loading ? (
        <Box sx={centeredContentFillSx}>
          <AILoading />
        </Box>
      ) : (
        <Box sx={columnGap12Sx}>
          <Box sx={getAiQuestionTabsSx(darkModeActive, tokens)}>
            {tabs.map((tab) => (
              <Box
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                sx={getAiQuestionTabSx(
                  activeTab === tab.key,
                  darkModeActive,
                  tokens,
                )}
              >
                {tab.label}
              </Box>
            ))}
          </Box>

          {activeTab === "hr" ? (
            <QuestionList
              questions={hrQuestions}
              onQuestionClick={onHrQuestionClick}
            />
          ) : (
            <QuestionList
              questions={technicalQuestions}
              onQuestionClick={onTechnicalQuestionClick}
            />
          )}
        </Box>
      )}
    </XCard>
  );
};
